import { useQuery } from '@apollo/client/react';
import loadable from '@loadable/component';
import { type FunctionComponent, useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {
	type CommonPromoFieldsFragment,
	type CategoryPlpContentQuery,
	type CategoryPlpContentQueryVariables
} from '../../../__generated__/graphql-client-types';
import { CategoryBrandLogo } from '../../../components/category-browse-components/category-brand-logo/category-brand-logo.component';
import { CategoryBrowseList } from '../../../components/category-browse-components/category-browse-list/category-browse-list.component';
import { LoadableCategoryContent } from '../../../components/category-browse-components/category-content/loadable-category-content.component';
import { CategoryImageTile, CategoryTextTile } from '../../../components/category-browse-components/category-tile/category-tile.component';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { CornerRibbon } from '../../../components/corner-ribbon/corner-ribbon.component';
import { FiveHundred } from '../../../components/error-components/500/500.component';
import { GroupContent } from '../../../components/group-content/group-content.component';
import { GroupContentSkeleton } from '../../../components/group-content-skeleton/group-content-skeleton.component';
import { PromoBanner } from '../../../components/promo-banner/promo-banner.component';
import { FacetDrawer } from '../../../components/search-components/facet-drawer/facet-drawer.component';
import { FacetFilterList } from '../../../components/search-components/facet-filter-list/facet-filter-list.component';
import { FacetList } from '../../../components/search-components/facet-list/facet-list.component';
import { FacetListSkeleton } from '../../../components/search-components/facet-list-skeleton/facet-list-skeleton.component';
import { SearchHeader } from '../../../components/search-components/search-header/search-header.component';
import { SearchProductCardSkeletonList } from '../../../components/search-components/search-product-card-skeleton/search-product-card-skeleton.component';
import { CATEGORY_PLP_PAGE_NAME, FEATURE_FLAGS, SITE_NAME } from '../../../constants/general';
import { CATEGORY_SORT_OPTIONS, SIMPLIFIED_CATEGORY_SORT_OPTIONS } from '../../../constants/search';
import { SEARCH_ENGINES } from '../../../constants/search-engines';
import { TrackedEvent, TrackedEventCase } from '../../../helpers/analytics/event-types';
import { buildGTMPromoBannerDisplay } from '../../../helpers/analytics/gtm/event-builders';
import { buildGTMSearch } from '../../../helpers/analytics/gtm/search-event-builders';
import {
	extractCategoryBrandName,
	formatDefaultCategoryMeta,
	isBrandCategory,
	mapPromoBannersByPriority
} from '../../../helpers/category-helper/category.helper';
import {
	generatePageViewSearchDataLayer,
	getCollectionNameFromResults,
	getTrackingSearchTermFromDropType
} from '../../../helpers/search-helper/search-analytics.helper';
import { useTrackEvent, useTrackPageView } from '../../../hooks/analytics/analytics.hooks';
import { useTrackImpressions } from '../../../hooks/analytics/impressions.hooks';
import { useSiteViewPreference } from '../../../hooks/apollo/employee/employee.hooks';
import { useGetSearchSource, useProductViewPreference, useSearchResults } from '../../../hooks/apollo/search/search.hooks';
import { useSetRumViewName } from '../../../hooks/datadog/rum.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useFeatures } from '../../../hooks/features/features.hooks';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { CATEGORY_PLP_CONTENT } from '../../../queries/content/content.queries';
import { type CategoryContentResult, type CategoryDropPageContent } from '../../../types/content.types';
import { type Category, type SearchRequestData } from '../../../types/search.types';
import { isChromatic } from '../../../utils/dom/chromatic.utils';
import { isCategoryCard } from '../../../utils/typeguards/content.typeguards';

const LoadableSearchResults = loadable(
	() => import(/* webpackChunkName: "search-results" */ '../../../components/search-components/search-results/search-results.component'),
	{
		resolveComponent: ({ SearchResults }) => SearchResults
	}
);

const LoadableSearchFooter = loadable(
	() => import(/* webpackChunkName: "search-footer" */ '../../../components/search-components/search-footer/search-footer.component'),
	{
		resolveComponent: ({ SearchFooter }) => SearchFooter
	}
);

const LoadableNoResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-search-results" */ '../../../components/search-components/no-search-results/no-search-results.component'
		),
	{
		resolveComponent: ({ NoResults }) => NoResults
	}
);

const LoadableNoFilteredSearchResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-filtered-search-results" */ '../../../components/search-components/no-filtered-search-results/no-filtered-search-results.component'
		),
	{
		resolveComponent: ({ NoFilteredSearchResults }) => NoFilteredSearchResults
	}
);

const LoadableSolrAlertNotification = loadable(
	() =>
		import(
			/* webpackChunkName: "solr-alert-notification" */ '../../../components/search-components/solr-alert-notification/solr-alert-notification.component'
		),
	{
		resolveComponent: ({ SolrAlertNotification }) => SolrAlertNotification
	}
);

const isCategoryDropContentResult = (result: CategoryContentResult | undefined): result is CategoryDropPageContent => {
	return result?.__typename === 'CategoryDropPageContent';
};

export type CategoryProductDropProps = {
	category: Category;
	promos: CommonPromoFieldsFragment[];
};

export const CategoryProductDrop: FunctionComponent<CategoryProductDropProps> = ({ category, promos }) => {
	const { title, name, metaData: categoryMetaData, subCategories } = category;
	const {
		results,
		page,
		pageSize,
		sortBy,
		previousResults,
		loading: productsLoading,
		categoryId,
		request,
		error,
		isNonstock
	} = useSearchResults();
	const { pathname, search } = useRouterLocation();
	const { baseCategory, businessCategory } = results || {};
	const categoryProductResults = productsLoading ? previousResults : results;
	const [isFacetDrawerOpen, setFacetDrawerOpen] = useState<boolean>(false);
	const showFacetDrawer = () => setFacetDrawerOpen(true);
	const hideFacetDrawer = () => setFacetDrawerOpen(false);
	const { productViewPreference } = useProductViewPreference();
	const { employee, showAsEmployee } = useSiteViewPreference();

	const trackEvent = useTrackEvent();
	const [hidePageSize, isSearchDropdownRestyle] = useFeatures<[boolean, boolean]>([
		FEATURE_FLAGS.HIDE_CATEGORY_PAGESIZE,
		FEATURE_FLAGS.SEARCH_DROPDOWN_RESTYLE
	]);

	// #region Construct Data
	const { data: contentData, loading: contentLoading } = useQuery<CategoryPlpContentQuery, CategoryPlpContentQueryVariables>(
		CATEGORY_PLP_CONTENT,
		{
			variables: {
				categoryId
			}
		}
	);

	const {
		topPrimary: topPromo,
		topSecondary: topSecondaryPromo,
		bottomSecondary: bottomSecondaryPromo
	} = mapPromoBannersByPriority(promos);
	const categoryContent = isCategoryDropContentResult(contentData?.categoryContent) ? contentData?.categoryContent : null;
	const searchEngineToUse = (productsLoading ? previousResults : results)?.searchEngine;
	const filteredGroupContent = (
		(searchEngineToUse && searchEngineToUse !== SEARCH_ENGINES.SOLR && categoryContent?.groupContent) ||
		[]
	).filter(Boolean);

	// #endregion

	useDyRecommendationContext({
		type: DyRecommendationContextType.CATEGORY_PAGE,
		data: (category.breadcrumbs || []).map((breadcrumb) => breadcrumb.name)
	});

	const categoryBrandName = extractCategoryBrandName(category) || '';
	const categoryType = isBrandCategory(category) ? 'brand' : 'category';
	const isValidBrand = categoryType === 'brand' && Boolean(categoryBrandName);
	const categoryBrandImage = category.categoryContentOverride?.categoryBrand?.brandImage;

	const pageName = CATEGORY_PLP_PAGE_NAME;
	const dropType = 'category';
	const searchSource = useGetSearchSource();
	const searchDataLayer = generatePageViewSearchDataLayer(
		pageName,
		dropType,
		results,
		page,
		pageSize,
		sortBy,
		undefined,
		searchSource,
		productViewPreference,
		isNonstock
	);

	const categoryContentOverride = category.categoryContentOverride;
	const categoryContentTrackingData = {
		brand: !categoryContentOverride.brand ? 'None' : categoryContentOverride.brand,
		businessCategory: !categoryContentOverride.businessCategory ? businessCategory ?? 'None' : categoryContentOverride.businessCategory,
		baseCategory: !categoryContentOverride.baseCategory ? baseCategory ?? 'None' : categoryContentOverride.baseCategory,
		collection: getCollectionNameFromResults(results)
	};

	useTrackPageView({ pageName, ...categoryContentTrackingData }, searchDataLayer, productsLoading);

	useTrackImpressions(
		TrackedEvent.CATEGORY_PLP_RESULTS_VIEW,
		getTrackingSearchTermFromDropType(dropType),
		request,
		results,
		productViewPreference,
		isNonstock
	);

	// Search tracking.
	const previousRequestRef = useRef<SearchRequestData | null>(null);
	useEffect(() => {
		if (!productsLoading && !error && request !== previousRequestRef.current) {
			const isNewSearch = request.categoryId !== previousRequestRef.current?.categoryId;
			trackEvent(
				buildGTMSearch(
					TrackedEvent.SEARCH,
					TrackedEventCase.CATEGORY_SEARCH_RESULTS,
					request,
					results,
					productViewPreference,
					employee.isAuthenticated,
					isNonstock,
					isNewSearch,
					undefined,
					undefined,
					category
				)
			);

			previousRequestRef.current = request;
		}
	}, [request, category, productsLoading, error, isNonstock, productViewPreference, results, trackEvent, employee]);

	const { ref: footerRef, inView: footerInView } = useInView({ triggerOnce: true });

	const { metaData: contentMetaData } = categoryContent || {};
	const { metaTitle: defaultMetaTitle, metaDescription: defaultMetaDescription } = formatDefaultCategoryMeta(
		isValidBrand ? categoryBrandName : title,
		SITE_NAME
	);

	const metaTitle = contentMetaData?.title || categoryMetaData?.title || defaultMetaTitle;

	const metaDescription = contentMetaData?.description || categoryMetaData?.description || defaultMetaDescription;

	// if no banner exists, then fire off tracking with an empty priority to indicate no banners are displayed
	useEffect(() => {
		if (promos && !topPromo && !topSecondaryPromo && !bottomSecondaryPromo) trackEvent(buildGTMPromoBannerDisplay());
	}, [promos]);

	useSetRumViewName('Category Product Drop');

	if (error) {
		return <FiveHundred />;
	}

	const { selectedFacetGroups = [], searchEngine, count = 0 } = categoryProductResults ?? {};

	return (
		<>
			{showAsEmployee && <LoadableSolrAlertNotification searchEngine={searchEngine ?? ''} />}
			<PageContainer
				canonicalURL={`${pathname}${search}`}
				metaDescription={metaDescription}
				pageTitle={metaTitle}
				breadcrumbs={category.breadcrumbs || []}
				showUpdateLocation={true}>
				<>
					<>
						<div>
							<SearchHeader
								title={title}
								sortOptions={isSearchDropdownRestyle ? SIMPLIFIED_CATEGORY_SORT_OPTIONS : CATEGORY_SORT_OPTIONS}
								onShowDrawer={showFacetDrawer}
							/>

							<div className="dn db-l mt2">
								<FacetFilterList />
							</div>
						</div>

						<div className="flex mt3 mt4-l" data-automation="category-product-list-page">
							<div className="w-25-l dn db-l">
								{isValidBrand && categoryBrandImage && <CategoryBrandLogo image={categoryBrandImage} />}
								{topSecondaryPromo && <PromoBanner {...topSecondaryPromo} />}
								{productsLoading && !previousResults ? <FacetListSkeleton /> : <FacetList />}{' '}
								{subCategories && (
									<CategoryBrowseList
										className="mt3"
										title={`Shop ${title.replace(/^\s*Shop\s+(All\s)?/i, '').trim()}`}
										titleClass="ph2 pv3 f4 font-medium bb b--theme-grey-light"
										items={subCategories}
										itemTransform={(categoryProps) => (
											<CategoryTextTile
												{...categoryProps}
												key={categoryProps.id}
												linkColor="primary"
												tileClass="db ph2 pv3 w-auto"
												className="bb b--theme-grey-light"
											/>
										)}
									/>
								)}
								{bottomSecondaryPromo && <PromoBanner {...bottomSecondaryPromo} />}
							</div>
							<div className="w-100 w-75-l h-100 relative">
								<div className="ml3-l">
									{topPromo && <PromoBanner {...topPromo} />}
									{contentLoading || (productsLoading && !previousResults) ? (
										<GroupContentSkeleton />
									) : (
										<GroupContent contentId={categoryContent?.id || ''} groups={filteredGroupContent} />
									)}
								</div>
								<div className="relative">
									{productsLoading ? (
										<SearchProductCardSkeletonList viewType={productViewPreference} />
									) : (
										<LoadableSearchResults viewType={productViewPreference} dropType={dropType} />
									)}
									{count === 0 && selectedFacetGroups.length > 0 && !productsLoading && (
										<LoadableNoFilteredSearchResults />
									)}
									{count === 0 && selectedFacetGroups.length === 0 && !productsLoading && (
										<LoadableNoResults type="category" />
									)}
									<div ref={footerRef} className="ml3-l">
										{(footerInView || isChromatic()) && <LoadableSearchFooter hidePageSize={hidePageSize} />}
									</div>
								</div>
								{categoryContent?.categories.map((shopSection, index) => {
									return (
										<div key={index} className="mv6">
											<h3>Shop by Related Categories</h3>
											<CategoryBrowseList
												className="flex-row-ns flex-wrap items-center justify-start ph0 w-auto w-100-ns"
												items={shopSection.items?.filter(isCategoryCard) || null}
												itemTransform={(categoryProps, itemIndex, info) => (
													<CategoryImageTile
														{...categoryProps}
														className="ph2 mb3"
														key={`${categoryProps.id}|${categoryProps.name}`}
														tileClass={`${!info.isLast ? 'bb' : ''}`}
													/>
												)}
											/>
										</div>
									);
								})}
								<LoadableCategoryContent content={categoryContent} categoryName={name} />
							</div>
						</div>
						{isFacetDrawerOpen && <FacetDrawer onFacetDrawerClose={hideFacetDrawer} />}
					</>

					<div id={`dy-recs-${categoryType}-page-1`} data-testid="dy-recs-placement-1" />
					{showAsEmployee && searchEngine && <CornerRibbon text={`${searchEngine} Search`} />}
				</>
			</PageContainer>
		</>
	);
};
